// RelationCreatorHelpers.js - Fonctionnalités interactives pour le RelationCreator
import { useTranslation } from 'react-i18next';

// Variable globale pour stocker la fonction de traduction
let globalT = null;

// Fonction pour initialiser la traduction
export const initializeTranslation = (t) => {
  globalT = t;
};

/**
 * Gestion des raccourcis clavier pour le RelationCreator
 */
export const setupKeyboardShortcuts = () => {
  const handleKeyDown = (event) => {
    // Ctrl + ? : Afficher les raccourcis
    if (event.ctrlKey && event.key === '/') {
      event.preventDefault();
      showKeyboardShortcuts();
    }
    
    // Ctrl + Enter : Créer la relation si possible
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault();
      const createBtn = document.querySelector('.create-relationship-btn');
      if (createBtn && !createBtn.disabled) {
        createBtn.click();
        showActionFeedback(globalT ? globalT('relationCreator.relationCreatedShortcut') : '🚀 Relation créée via raccourci!', 'success');
      } else {
        showActionFeedback(globalT ? globalT('relationCreator.pleaseCompleteFields') : '⚠️ Veuillez compléter tous les champs requis', 'warning');
      }
    }
    
    // Échap : Fermer les overlays
    if (event.key === 'Escape') {
      hideKeyboardShortcuts();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  
  // Retourner une fonction de nettoyage
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * Afficher l'overlay des raccourcis clavier
 */
export const showKeyboardShortcuts = () => {
  // Supprimer l'overlay existant s'il y en a un
  const existingOverlay = document.querySelector('.keyboard-shortcuts-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }

  // Créer le nouvel overlay
  const overlay = document.createElement('div');
  overlay.className = 'keyboard-shortcuts-overlay';
  overlay.innerHTML = `
    <div class="shortcuts-panel">
      <div class="shortcuts-title">${globalT ? globalT('relationCreator.keyboardShortcuts') : '🎹 Raccourcis Clavier'}</div>
      <div class="shortcut-item">
        <span class="shortcut-desc">${globalT ? globalT('relationCreator.showHideHelp') : 'Afficher/Masquer cette aide'}</span>
        <div class="shortcut-keys">
          <span class="shortcut-key">Ctrl</span>
          <span class="shortcut-key">?</span>
        </div>
      </div>
      <div class="shortcut-item">
        <span class="shortcut-desc">${globalT ? globalT('relationCreator.createRelationShortcut') : 'Créer la relation'}</span>
        <div class="shortcut-keys">
          <span class="shortcut-key">Ctrl</span>
          <span class="shortcut-key">Enter</span>
        </div>
      </div>
      <div class="shortcut-item">
        <span class="shortcut-desc">${globalT ? globalT('relationCreator.closeOverlays') : 'Fermer les overlays'}</span>
        <div class="shortcut-keys">
          <span class="shortcut-key">Échap</span>
        </div>
      </div>
      <div class="shortcut-item">
        <span class="shortcut-desc">${globalT ? globalT('relationCreator.quickNavigation') : 'Navigation rapide dans les champs'}</span>
        <div class="shortcut-keys">
          <span class="shortcut-key">Tab</span>
        </div>
      </div>
    </div>
  `;

  // Ajouter l'événement de fermeture
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      hideKeyboardShortcuts();
    }
  });

  document.body.appendChild(overlay);
  
  // Afficher avec animation
  setTimeout(() => {
    overlay.classList.add('show');
  }, 10);
};

/**
 * Masquer l'overlay des raccourcis clavier
 */
export const hideKeyboardShortcuts = () => {
  const overlay = document.querySelector('.keyboard-shortcuts-overlay');
  if (overlay) {
    overlay.classList.remove('show');
    setTimeout(() => {
      overlay.remove();
    }, 300);
  }
};

/**
 * Afficher un feedback d'action
 */
export const showActionFeedback = (message, type = 'success') => {
  // Supprimer le feedback existant
  const existingFeedback = document.querySelector('.action-feedback');
  if (existingFeedback) {
    existingFeedback.remove();
  }

  // Créer le nouveau feedback
  const feedback = document.createElement('div');
  feedback.className = `action-feedback ${type}`;
  
  const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : '⚠️';
  feedback.innerHTML = `
    <span class="feedback-icon">${icon}</span>
    <span>${message}</span>
  `;

  document.body.appendChild(feedback);
  
  // Afficher avec animation
  setTimeout(() => {
    feedback.classList.add('show');
  }, 10);

  // Masquer automatiquement après 3 secondes
  setTimeout(() => {
    feedback.classList.remove('show');
    setTimeout(() => {
      feedback.remove();
    }, 400);
  }, 3000);
};

/**
 * Ajouter des suggestions intelligentes
 */
export const setupSmartSuggestions = (relationshipTypes) => {
  const relationshipSelect = document.querySelector('.modern-select');
  if (!relationshipSelect) return;

  relationshipSelect.addEventListener('focus', () => {
    const suggestions = generateSmartSuggestions(relationshipTypes);
    showSuggestions(relationshipSelect, suggestions);
  });

  relationshipSelect.addEventListener('blur', () => {
    setTimeout(() => {
      hideSuggestions(relationshipSelect);
    }, 200);
  });
};

/**
 * Générer des suggestions intelligentes
 */
const generateSmartSuggestions = (relationshipTypes) => {
  const commonTypes = [
    { type: 'DEPENDS_ON', confidence: 0.9, icon: '🔗', reason: globalT ? globalT('relationCreator.mostCommonType') : 'Type le plus couramment utilisé' },
    { type: 'USES', confidence: 0.8, icon: '🎯', reason: globalT ? globalT('relationCreator.frequentServiceRelation') : 'Relation fréquente entre services' },
    { type: 'CONTAINS', confidence: 0.7, icon: '📦', reason: globalT ? globalT('relationCreator.hierarchicalRelations') : 'Pour les relations hiérarchiques' },
    { type: 'IMPLEMENTS', confidence: 0.6, icon: '⚙️', reason: globalT ? globalT('relationCreator.implementationRelations') : 'Pour les relations d\'implémentation' }
  ];

  return commonTypes;
};

/**
 * Afficher les suggestions
 */
const showSuggestions = (element, suggestions) => {
  const container = element.parentElement;
  if (!container) return;

  // Supprimer les suggestions existantes
  const existingSuggestions = container.querySelector('.smart-suggestions');
  if (existingSuggestions) {
    existingSuggestions.remove();
  }

  // Créer le conteneur de suggestions
  const suggestionContainer = document.createElement('div');
  suggestionContainer.className = 'smart-suggestions';
  
  suggestions.forEach(suggestion => {
    const suggestionItem = document.createElement('div');
    suggestionItem.className = 'suggestion-item';
    suggestionItem.innerHTML = `
      <span class="suggestion-icon">${suggestion.icon}</span>
      <span class="suggestion-text">${suggestion.type}</span>
      <span class="suggestion-confidence">${Math.round(suggestion.confidence * 100)}%</span>
    `;
    
    suggestionItem.addEventListener('click', () => {
      element.value = suggestion.type;
      element.dispatchEvent(new Event('change', { bubbles: true }));
      hideSuggestions(element);
      showActionFeedback(`✨ Type "${suggestion.type}" ${globalT ? globalT('relationCreator.suggestionSelected') : 'sélectionné via suggestion!'}`, 'success');
    });
    
    suggestionContainer.appendChild(suggestionItem);
  });

  container.appendChild(suggestionContainer);
  
  // Afficher avec animation
  setTimeout(() => {
    suggestionContainer.classList.add('show');
  }, 10);
};

/**
 * Masquer les suggestions
 */
const hideSuggestions = (element) => {
  const container = element.parentElement;
  if (!container) return;

  const suggestions = container.querySelector('.smart-suggestions');
  if (suggestions) {
    suggestions.classList.remove('show');
    setTimeout(() => {
      suggestions.remove();
    }, 300);
  }
};

/**
 * Initialiser toutes les fonctionnalités interactives
 */
export const initializeRelationCreatorFeatures = (relationshipTypes = [], t = null) => {
  // Initialiser la traduction
  if (t) {
    initializeTranslation(t);
  }
  
  // Configuration des raccourcis clavier
  const cleanupKeyboard = setupKeyboardShortcuts();
  
  // Configuration des suggestions intelligentes
  setupSmartSuggestions(relationshipTypes);
  
  // Animation de chargement de page
  const container = document.querySelector('.relationship-creator-container');
  if (container) {
    container.style.opacity = '0';
    container.style.transform = 'translateY(20px)';
    
    setTimeout(() => {
      container.style.transition = 'all 0.6s ease-out';
      container.style.opacity = '1';
      container.style.transform = 'translateY(0)';
    }, 100);
  }
  
  // Message de bienvenue
  setTimeout(() => {
    showActionFeedback(globalT ? globalT('relationCreator.welcomeMessage') : '🎉 Interface de création de relations chargée! Utilisez Ctrl+? pour voir les raccourcis.', 'success');
  }, 1000);
  
  // Retourner une fonction de nettoyage
  return () => {
    cleanupKeyboard();
  };
};

// Fonctionnalité d'auto-complétion améliorée
export const enhanceAutoComplete = () => {
  const searchInput = document.querySelector('.nodes-search-input');
  if (!searchInput) return;

  let debounceTimer;
  
  searchInput.addEventListener('input', (e) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const value = e.target.value;
      if (value.length >= 2) {
        // Simuler une recherche intelligente
        const matches = document.querySelectorAll('.target-select option').length - 1;
        const counter = document.querySelector('.match-counter');
        if (counter) {
          counter.textContent = matches;
          counter.style.opacity = '1';
          counter.style.transform = 'scale(1)';
        }
      }
    }, 300);
  });
};